import {useEffect, useMemo, useState} from 'react';

import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {useQueryClient} from '@tanstack/react-query';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {getCurrentYear} from 'presentation/utils/getCurrentYear';
import {useGetHistoryYearsQuery} from '../../../queries/useGetHistoryYearsQuery';
import {useGetIncomeTaxPdfQuery} from '../queries/useGetIncomeTaxPdfQuery';
import {usePostIncomeTaxDetailsMutation} from '../queries/usePostIncomeTaxDetailsMutation';

function useIncomeTax() {
	const queryClient = useQueryClient();
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {
		data: availableYears,
		isLoading: isLoadingAvailableYears,
		isFetching: isFetchingAvailableYears,
	} = useGetHistoryYearsQuery({
		isShowCurrentYear: false,
	});

	const [shouldOpenPdf, setShouldOpenPdf] = useState(false);

	const currentYear = useMemo(() => new Date().getFullYear().toString(), []);

	const selectedYear = useMemo(() => {
		const yearParams = SEARCH_PARAMS.year || availableYears?.[0];
		return (
			availableYears?.find(availableYear => String(availableYear) === String(yearParams)) ||
			availableYears?.[0]
		);
	}, [SEARCH_PARAMS.year, availableYears]);

	const dateRange = useMemo(() => {
		const hasAvailableYears = availableYears && availableYears.length > 0;

		const minDate = hasAvailableYears
			? new Date(Number(availableYears[availableYears.length - 1]), 1, 1)
			: new Date(Number(currentYear), 1, 1);

		const maxDate = hasAvailableYears
			? new Date(Number(availableYears[0]), 1, 1)
			: new Date(Number(currentYear), 1, 1);

		return {minDate, maxDate};
	}, [availableYears, currentYear]);

	const formattedYear = useMemo(
		() =>
			getCurrentYear({
				year: selectedYear ?? '',
				maxDate: dateRange.maxDate,
				minDate: dateRange.minDate,
			}),
		[selectedYear, dateRange.maxDate, dateRange.minDate],
	);

	const {
		data: incomeTaxData,
		isLoading: isLoadingIncomeTaxData,
		isFetching: isFetchingIncomeTaxData,
		isError: isErrorIncomeTaxData,
	} = usePostIncomeTaxDetailsMutation(selectedYear);

	const loadingStates = useMemo(
		() => ({
			isLoadingIncomeTaxData: isLoadingIncomeTaxData || isFetchingIncomeTaxData,
			isLoadingAvailableYears: isLoadingAvailableYears || isFetchingAvailableYears,
		}),
		[
			isLoadingIncomeTaxData,
			isFetchingIncomeTaxData,
			isLoadingAvailableYears,
			isFetchingAvailableYears,
		],
	);

	const emptyStates = useMemo(
		() => ({
			isEmptyAvailableYears: !availableYears || availableYears.length === 0,
			isEmptyIncomeTaxData: !incomeTaxData || incomeTaxData?.impostoDeRenda?.length === 0,
		}),
		[availableYears, incomeTaxData],
	);

	const {
		data: pdfData,
		refetch: fetchPdf,
		isLoading: isLoadingIncomeTaxPdf,
		isFetching: isFetchingIncomeTaxPdf,
		isError: isErrorIncomeTaxPdf,
	} = useGetIncomeTaxPdfQuery({dtExercicio: selectedYear ?? ''});

	async function downloadPdf() {
		try {
			if (pdfData) {
				window.open(URL.createObjectURL(pdfData));
			} else {
				await fetchPdf();
				setShouldOpenPdf(true);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
		}
	}

	useEffect(() => {
		if (pdfData && shouldOpenPdf) {
			window.open(URL.createObjectURL(pdfData));
			setShouldOpenPdf(false);
		}
		if (isErrorIncomeTaxPdf) {
			setShouldOpenPdf(false);
		}
	}, [pdfData, shouldOpenPdf, isErrorIncomeTaxPdf]);

	const handleToggleDownloadErrorState = () => {
		queryClient.resetQueries({
			queryKey: [QUERY_KEYS.INCOME_TAX_PDF, {dtExercicio: selectedYear}],
		});
	};

	return {
		SEARCH_PARAMS,
		setSearchParams,
		availableYears,
		incomeTaxData,
		...loadingStates,
		isErrorIncomeTaxData,
		downloadPdf,
		isDownloading: isLoadingIncomeTaxPdf || isFetchingIncomeTaxPdf,
		downloadErrorState: isErrorIncomeTaxPdf,
		year: selectedYear,
		formattedYear,
		...emptyStates,
		handleToggleDownloadErrorState,
	};
}

export {useIncomeTax};
